import { ComponentDefinition } from '../../core/types/inspector.types';
import {
  DEFAULT_SPINE_RENDERER_DATA,
  DEFAULT_SPINE_ANIMATOR_DATA,
} from '../../core/types/spine.types';

export const BUILT_IN_COMPONENTS: ComponentDefinition[] = [
  {
    type: 'transform',
    name: 'Transform',
    icon: '📐',
    category: 'Core',
    removable: false,
    properties: [
      { name: 'position.x', type: 'number', label: 'Position X', step: 1 },
      { name: 'position.y', type: 'number', label: 'Position Y', step: 1 },
      { name: 'rotation', type: 'number', label: 'Rotation', min: -360, max: 360, step: 1 },
      { name: 'scale.x', type: 'number', label: 'Scale X', step: 0.1 },
      { name: 'scale.y', type: 'number', label: 'Scale Y', step: 0.1 },
      { name: 'anchor.x', type: 'number', label: 'Anchor X', min: 0, max: 1, step: 0.1 },
      { name: 'anchor.y', type: 'number', label: 'Anchor Y', min: 0, max: 1, step: 0.1 },
    ],
  },
  {
    type: 'sprite-renderer',
    name: 'Sprite Renderer',
    icon: '🖼️',
    category: 'Rendering',
    removable: true,
    properties: [
      { name: 'textureId', type: 'texture', label: 'Texture' },
      { name: 'texturePath', type: 'string', label: 'Texture Path', hidden: true },
      { name: 'textureWidth', type: 'number', label: 'Width', hidden: true },
      { name: 'textureHeight', type: 'number', label: 'Height', hidden: true },
      { name: 'tint', type: 'color', label: 'Tint', defaultValue: '#ffffff' },
      { name: 'alpha', type: 'slider', label: 'Alpha', min: 0, max: 1, step: 0.01, defaultValue: 1 },
      { name: 'flipX', type: 'boolean', label: 'Flip X', defaultValue: false },
      { name: 'flipY', type: 'boolean', label: 'Flip Y', defaultValue: false },
      { name: 'sortingLayer', type: 'dropdown', label: 'Sorting Layer', options: [
        { value: 'default', label: 'Default' },
        { value: 'background', label: 'Background' },
        { value: 'foreground', label: 'Foreground' },
        { value: 'ui', label: 'UI' },
      ]},
      { name: 'sortingOrder', type: 'number', label: 'Sorting Order', step: 1, defaultValue: 0 },
    ],
  },
  {
    type: 'box-collider',
    name: 'Box Collider',
    icon: '📦',
    category: 'Physics',
    removable: true,
    properties: [
      { name: 'isTrigger', type: 'boolean', label: 'Is Trigger', defaultValue: false },
      { name: 'width', type: 'number', label: 'Width', min: 1, step: 1, defaultValue: 32 },
      { name: 'height', type: 'number', label: 'Height', min: 1, step: 1, defaultValue: 32 },
      { name: 'offsetX', type: 'number', label: 'Offset X', step: 1, defaultValue: 0 },
      { name: 'offsetY', type: 'number', label: 'Offset Y', step: 1, defaultValue: 0 },
      { name: 'showInScene', type: 'boolean', label: 'Show in Scene', defaultValue: true },
    ],
  },
  {
    type: 'circle-collider',
    name: 'Circle Collider',
    icon: '⭕',
    category: 'Physics',
    removable: true,
    properties: [
      { name: 'isTrigger', type: 'boolean', label: 'Is Trigger', defaultValue: false },
      { name: 'radius', type: 'number', label: 'Radius', min: 1, step: 1, defaultValue: 16 },
      { name: 'offsetX', type: 'number', label: 'Offset X', step: 1, defaultValue: 0 },
      { name: 'offsetY', type: 'number', label: 'Offset Y', step: 1, defaultValue: 0 },
      { name: 'showInScene', type: 'boolean', label: 'Show in Scene', defaultValue: true },
    ],
  },
  {
    type: 'polygon-collider',
    name: 'Polygon Collider',
    icon: '🔷',
    category: 'Physics',
    removable: true,
    properties: [
      { name: 'isTrigger', type: 'boolean', label: 'Is Trigger', defaultValue: false },
      { name: 'points', type: 'string', label: 'Points', defaultValue: '[]' },
      { name: 'offsetX', type: 'number', label: 'Offset X', step: 1, defaultValue: 0 },
      { name: 'offsetY', type: 'number', label: 'Offset Y', step: 1, defaultValue: 0 },
      { name: 'showInScene', type: 'boolean', label: 'Show in Scene', defaultValue: true },
    ],
  },
  {
    type: 'rigidbody',
    name: 'Rigidbody',
    icon: '🎱',
    category: 'Physics',
    removable: true,
    properties: [
      { name: 'bodyType', type: 'dropdown', label: 'Body Type', options: [
        { value: 'dynamic', label: 'Dynamic' },
        { value: 'static', label: 'Static' },
        { value: 'kinematic', label: 'Kinematic' },
      ], defaultValue: 'dynamic' },
      { name: 'mass', type: 'number', label: 'Mass', min: 0.001, step: 0.1, defaultValue: 1 },
      { name: 'gravityScale', type: 'number', label: 'Gravity Scale', step: 0.1, defaultValue: 1 },
      { name: 'linearDamping', type: 'slider', label: 'Linear Damping', min: 0, max: 10, step: 0.1, defaultValue: 0 },
      { name: 'angularDamping', type: 'slider', label: 'Angular Damping', min: 0, max: 10, step: 0.1, defaultValue: 0.05 },
      { name: 'fixedRotation', type: 'boolean', label: 'Fixed Rotation', defaultValue: false },
    ],
  },
  {
    type: 'audio-source',
    name: 'Audio Source',
    icon: '🔊',
    category: 'Audio',
    removable: true,
    properties: [
      { name: 'audioClip', type: 'reference', label: 'Audio Clip' },
      { name: 'volume', type: 'slider', label: 'Volume', min: 0, max: 1, step: 0.01, defaultValue: 1 },
      { name: 'pitch', type: 'slider', label: 'Pitch', min: 0.1, max: 3, step: 0.1, defaultValue: 1 },
      { name: 'loop', type: 'boolean', label: 'Loop', defaultValue: false },
      { name: 'playOnAwake', type: 'boolean', label: 'Play On Awake', defaultValue: false },
      { name: 'spatialBlend', type: 'slider', label: 'Spatial Blend', min: 0, max: 1, step: 0.1, defaultValue: 0 },
    ],
  },
  {
    type: 'animator',
    name: 'Animator',
    icon: '🎬',
    category: 'Rendering',
    removable: true,
    properties: [
      { name: 'animationClip', type: 'reference', label: 'Animation Clip' },
      { name: 'playOnAwake', type: 'boolean', label: 'Play On Awake', defaultValue: true },
      { name: 'speed', type: 'slider', label: 'Speed', min: 0, max: 3, step: 0.1, defaultValue: 1 },
      { name: 'loop', type: 'boolean', label: 'Loop', defaultValue: true },
    ],
  },
  {
    type: 'particle-system',
    name: 'Particle System',
    icon: '✨',
    category: 'Rendering',
    removable: true,
    properties: [
      { name: 'maxParticles', type: 'number', label: 'Max Particles', min: 1, step: 1, defaultValue: 100 },
      { name: 'emissionRate', type: 'number', label: 'Emission Rate', min: 0, step: 1, defaultValue: 10 },
      { name: 'lifetime', type: 'number', label: 'Lifetime', min: 0.1, step: 0.1, defaultValue: 1 },
      { name: 'startSpeed', type: 'number', label: 'Start Speed', step: 1, defaultValue: 5 },
      { name: 'startSize', type: 'number', label: 'Start Size', min: 0.1, step: 0.1, defaultValue: 1 },
      { name: 'startColor', type: 'color', label: 'Start Color', defaultValue: '#ffffff' },
      { name: 'playOnAwake', type: 'boolean', label: 'Play On Awake', defaultValue: true },
      { name: 'loop', type: 'boolean', label: 'Loop', defaultValue: true },
    ],
  },
  {
    type: 'text-renderer',
    name: 'Text Renderer',
    icon: '🔤',
    category: 'Rendering',
    removable: true,
    properties: [
      { name: 'text', label: 'Text', type: 'string', defaultValue: 'New Text' },
      { name: 'fontFamily', label: 'Font Family', type: 'string', defaultValue: 'Arial' },
      { name: 'fontSize', label: 'Font Size', type: 'number', defaultValue: 24, min: 1, max: 256, step: 1 },
      { name: 'fontWeight', label: 'Font Weight', type: 'dropdown', defaultValue: 'normal', options: [
        { value: 'normal', label: 'Normal' },
        { value: 'bold', label: 'Bold' },
        { value: 'lighter', label: 'Lighter' },
        { value: 'bolder', label: 'Bolder' },
      ]},
      { name: 'fontStyle', label: 'Font Style', type: 'dropdown', defaultValue: 'normal', options: [
        { value: 'normal', label: 'Normal' },
        { value: 'italic', label: 'Italic' },
        { value: 'oblique', label: 'Oblique' },
      ]},
      { name: 'fill', label: 'Color', type: 'color', defaultValue: '#ffffff' },
      { name: 'align', label: 'Align', type: 'dropdown', defaultValue: 'left', options: [
        { value: 'left', label: 'Left' },
        { value: 'center', label: 'Center' },
        { value: 'right', label: 'Right' },
        { value: 'justify', label: 'Justify' },
      ]},
      { name: 'wordWrap', label: 'Word Wrap', type: 'boolean', defaultValue: false },
      { name: 'wordWrapWidth', label: 'Wrap Width', type: 'number', defaultValue: 200, min: 1, step: 1 },
      { name: 'letterSpacing', label: 'Letter Spacing', type: 'number', defaultValue: 0, step: 0.5 },
      { name: 'lineHeight', label: 'Line Height', type: 'number', defaultValue: 0, min: 0, step: 1 },
    ],
  },
  {
    type: 'graphics-renderer',
    name: 'Graphics Renderer',
    icon: '🖌️',
    category: 'Rendering',
    removable: true,
    properties: [
      { name: 'shape', label: 'Shape', type: 'dropdown', defaultValue: 'rectangle', options: [
        { value: 'rectangle', label: 'Rectangle' },
        { value: 'rounded-rectangle', label: 'Rounded Rectangle' },
        { value: 'circle', label: 'Circle' },
        { value: 'ellipse', label: 'Ellipse' },
        { value: 'polygon', label: 'Polygon' },
        { value: 'line', label: 'Line' },
        { value: 'star', label: 'Star' },
      ]},
      { name: 'fillColor', label: 'Fill Color', type: 'color', defaultValue: '#ffffff' },
      { name: 'fillAlpha', label: 'Fill Alpha', type: 'slider', defaultValue: 1, min: 0, max: 1, step: 0.01 },
      { name: 'strokeColor', label: 'Stroke Color', type: 'color', defaultValue: '#000000' },
      { name: 'strokeWidth', label: 'Stroke Width', type: 'number', defaultValue: 0, min: 0, step: 1 },
      { name: 'strokeAlpha', label: 'Stroke Alpha', type: 'slider', defaultValue: 1, min: 0, max: 1, step: 0.01 },
      { name: 'width', label: 'Width', type: 'number', defaultValue: 100, min: 1, step: 1 },
      { name: 'height', label: 'Height', type: 'number', defaultValue: 100, min: 1, step: 1 },
      { name: 'radius', label: 'Radius', type: 'number', defaultValue: 50, min: 1, step: 1 },
      { name: 'cornerRadius', label: 'Corner Radius', type: 'number', defaultValue: 0, min: 0, step: 1 },
      { name: 'points', label: 'Points (Star/Polygon)', type: 'number', defaultValue: 5, min: 3, max: 20, step: 1 },
    ],
  },
  {
    type: 'camera',
    name: 'Camera',
    icon: '📷',
    category: 'Rendering',
    removable: true,
    properties: [
      { name: 'isMain', label: 'Main Camera', type: 'boolean', defaultValue: true },
      { name: 'backgroundColor', label: 'Background Color', type: 'color', defaultValue: '#1a1a2e' },
      { name: 'zoom', label: 'Zoom', type: 'slider', defaultValue: 1, min: 0.1, max: 10, step: 0.1 },
      { name: 'viewportWidth', label: 'Viewport Width', type: 'number', defaultValue: 720, min: 1, step: 1 },
      { name: 'viewportHeight', label: 'Viewport Height', type: 'number', defaultValue: 1560, min: 1, step: 1 },
      { name: 'followTarget', label: 'Follow Target', type: 'reference', defaultValue: null },
      { name: 'followSmoothing', label: 'Follow Smoothing', type: 'slider', defaultValue: 0.1, min: 0, max: 1, step: 0.01 },
      { name: 'bounds', label: 'Constrain to Bounds', type: 'boolean', defaultValue: false },
      { name: 'boundsMinX', label: 'Bounds Min X', type: 'number', defaultValue: 0, step: 1 },
      { name: 'boundsMinY', label: 'Bounds Min Y', type: 'number', defaultValue: 0, step: 1 },
      { name: 'boundsMaxX', label: 'Bounds Max X', type: 'number', defaultValue: 720, step: 1 },
      { name: 'boundsMaxY', label: 'Bounds Max Y', type: 'number', defaultValue: 1560, step: 1 },
    ],
  },
  {
    type: 'light',
    name: 'Light',
    icon: '💡',
    category: 'Rendering',
    removable: true,
    properties: [
      { name: 'lightType', label: 'Type', type: 'dropdown', defaultValue: 'point', options: [
        { value: 'point', label: 'Point' },
        { value: 'ambient', label: 'Ambient' },
        { value: 'directional', label: 'Directional' },
        { value: 'spot', label: 'Spot' },
      ]},
      { name: 'color', label: 'Color', type: 'color', defaultValue: '#ffffff' },
      { name: 'intensity', label: 'Intensity', type: 'slider', defaultValue: 1, min: 0, max: 10, step: 0.1 },
      { name: 'radius', label: 'Radius', type: 'number', defaultValue: 200, min: 1, step: 1 },
      { name: 'falloff', label: 'Falloff', type: 'slider', defaultValue: 1, min: 0, max: 3, step: 0.1 },
      { name: 'angle', label: 'Angle (Spot)', type: 'slider', defaultValue: 45, min: 1, max: 180, step: 1 },
      { name: 'direction', label: 'Direction (Directional)', type: 'number', defaultValue: 0, min: -180, max: 180, step: 1 },
      { name: 'castShadows', label: 'Cast Shadows', type: 'boolean', defaultValue: false },
      { name: 'shadowSoftness', label: 'Shadow Softness', type: 'slider', defaultValue: 0.5, min: 0, max: 1, step: 0.01 },
    ],
  },
  {
    type: 'spine-renderer',
    name: 'Spine Renderer',
    icon: '🎭',
    category: 'Rendering',
    removable: true,
    properties: [
      {
        name: 'spineAssetId',
        type: 'spine-asset',
        label: 'Spine Asset',
      },
      {
        name: 'skinName',
        type: 'spine-skin',
        label: 'Skin',
        defaultValue: 'default',
      },
      {
        name: 'tint',
        type: 'color',
        label: 'Tint',
        defaultValue: '#ffffff',
      },
      {
        name: 'alpha',
        type: 'slider',
        label: 'Alpha',
        min: 0,
        max: 1,
        step: 0.01,
        defaultValue: 1,
      },
      {
        name: 'premultipliedAlpha',
        type: 'boolean',
        label: 'Premultiplied Alpha',
        defaultValue: true,
      },
      {
        name: 'sortingLayer',
        type: 'dropdown',
        label: 'Sorting Layer',
        options: [
          { value: 'default', label: 'Default' },
          { value: 'background', label: 'Background' },
          { value: 'foreground', label: 'Foreground' },
          { value: 'ui', label: 'UI' },
        ],
        defaultValue: 'default',
      },
      {
        name: 'sortingOrder',
        type: 'number',
        label: 'Sorting Order',
        step: 1,
        defaultValue: 0,
      },
      {
        name: 'showBones',
        type: 'boolean',
        label: 'Show Bones (Debug)',
        defaultValue: false,
      },
      {
        name: 'showSlots',
        type: 'boolean',
        label: 'Show Slots (Debug)',
        defaultValue: false,
      },
      {
        name: 'showBoundingBoxes',
        type: 'boolean',
        label: 'Show Bounding Boxes',
        defaultValue: false,
      },
    ],
  },
  {
    type: 'spine-animator',
    name: 'Spine Animator',
    icon: '🎬',
    category: 'Rendering',
    removable: true,
    properties: [
      {
        name: 'playOnAwake',
        type: 'boolean',
        label: 'Play On Awake',
        defaultValue: false,
      },
      {
        name: 'defaultAnimation',
        type: 'spine-animation',
        label: 'Default Animation',
      },
      {
        name: 'defaultLoop',
        type: 'boolean',
        label: 'Loop',
        defaultValue: true,
      },
      {
        name: 'speed',
        type: 'slider',
        label: 'Time Scale',
        min: 0.1,
        max: 2,
        step: 0.1,
        defaultValue: 1,
      },
      {
        name: 'defaultMixDuration',
        type: 'slider',
        label: 'Mix Duration',
        min: 0,
        max: 1,
        step: 0.05,
        defaultValue: 0.2,
      },
      {
        name: 'enableEvents',
        type: 'boolean',
        label: 'Enable Events',
        defaultValue: true,
      },
      {
        name: 'animationList',
        type: 'spine-animation-list',
        label: 'Animations',
      },
    ],
  },
  // =========================================================================
  // Cascade Slot
  // =========================================================================
  {
    type: 'cascade-slot-part',
    name: 'Cascade Slot Part',
    icon: '🧩',
    category: 'Slot',
    removable: false,
    properties: [
      { name: 'partType', type: 'string', label: 'Part Type', defaultValue: 'header' },
    ],
  },
  {
    type: 'backend-game-config',
    name: 'Backend Game Config',
    icon: '🎮',
    category: 'Slot',
    removable: false,
    properties: [
      { name: 'gameName', type: 'string', label: 'Game Name', defaultValue: '' },
      { name: 'gameId', type: 'string', label: 'Game ID', defaultValue: '' },
      { name: 'name', type: 'string', label: 'Display Name', defaultValue: '' },
      { name: 'version', type: 'string', label: 'Version', defaultValue: '' },
      { name: 'description', type: 'string', label: 'Description', defaultValue: '' },
      { name: 'activeMode', type: 'number', label: 'Active Mode', defaultValue: 0 },
    ],
  },
  {
    type: 'cascade-slot-config',
    name: 'Cascade Slot Config',
    icon: '🎰',
    category: 'Slot',
    removable: false,
    properties: [
      { name: 'rows', type: 'number', label: 'Rows', min: 4, max: 10, step: 1, defaultValue: 6 },
      { name: 'columns', type: 'number', label: 'Columns', min: 4, max: 8, step: 1, defaultValue: 5 },
      { name: 'symbolWidth', type: 'number', label: 'Symbol Width', min: 50, max: 300, step: 1, defaultValue: 120 },
      { name: 'symbolHeight', type: 'number', label: 'Symbol Height', min: 50, max: 300, step: 1, defaultValue: 100 },
      { name: 'symbolSpacing', type: 'number', label: 'Symbol Spacing', min: 0, max: 20, step: 1, defaultValue: 4 },
      { name: 'minMatchCount', type: 'number', label: 'Min Match Count', min: 4, max: 12, step: 1, defaultValue: 8 },
      { name: 'cascadeEnabled', type: 'boolean', label: 'Cascade Enabled', defaultValue: true },
      { name: 'cascadeDirection', type: 'dropdown', label: 'Cascade Direction', options: [
        { value: 'vertical', label: 'Vertical' },
        { value: 'horizontal', label: 'Horizontal' },
      ], defaultValue: 'horizontal' },
      { name: 'cascadeAnimSpeed', type: 'number', label: 'Cascade Anim Speed (ms)', min: 100, max: 1000, step: 50, defaultValue: 300 },
      { name: 'spinDuration', type: 'number', label: 'Spin Duration (s)', min: 0.1, max: 3, step: 0.1, defaultValue: 0.5 },
      { name: 'turboMultiplier', type: 'number', label: 'Turbo Multiplier', min: 1, max: 10, step: 1, defaultValue: 3 },
      { name: 'startingBalance', type: 'number', label: 'Starting Balance', min: 100, max: 100000, step: 100, defaultValue: 10000 },
      { name: 'buyFeatureEnabled', type: 'boolean', label: 'Buy Feature', defaultValue: true },
      { name: 'buyFeatureCostMultiplier', type: 'number', label: 'Buy Cost (x bet)', min: 10, max: 500, step: 10, defaultValue: 100 },
      { name: 'maxWinMultiplier', type: 'number', label: 'Max Win (x bet)', min: 1000, max: 100000, step: 1000, defaultValue: 50000 },
    ],
  },
];

export const getComponentDefinition = (type: string): ComponentDefinition | undefined => {
  return BUILT_IN_COMPONENTS.find((c) => c.type === type);
};

export const getComponentsByCategory = (category: string): ComponentDefinition[] => {
  return BUILT_IN_COMPONENTS.filter((c) => c.category === category);
};

export const getAddableComponents = (): ComponentDefinition[] => {
  return BUILT_IN_COMPONENTS.filter((c) => c.type !== 'transform');
};
